/**
 * Header — ported verbatim from oakallow-nextjs/components/Header.tsx.
 * Sign-out hits `/auth/logout`, which is now served by the Hono worker —
 * the URL and behavior are unchanged from source.
 *
 * The signed-in user dropdown lives here (top-right, next to the theme
 * switcher) so the team switcher can sit at the top of the sidebar where
 * it belongs. Same pattern Slack/Linear/GitHub use.
 */

import { useEffect, useRef, useState } from 'react';
import { Sun, Moon, Eye, LogOut, Menu } from 'lucide-react';

type Theme = 'light' | 'dark' | 'high-contrast';

export default function Header({ onToggleSidebar }: { onToggleSidebar?: () => void }) {
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [signedInName, setSignedInName] = useState('');
  const [signedInEmail, setSignedInEmail] = useState('');
  const [signedInAuthMethod, setSignedInAuthMethod] = useState('');
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Pull the current developer's display name + email from /api/me. The
  // sidebar already does this; we duplicate the fetch here so the header
  // stays self-contained and renders correctly even if the sidebar isn't
  // mounted (eg. on a future full-screen page).
  useEffect(() => {
    let cancelled = false;
    fetch('/api/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.developer) return;
        setSignedInName((data.developer.name as string) || '');
        setSignedInEmail((data.developer.email as string) || '');
        setSignedInAuthMethod((data.developer.auth_method as string) || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!showUserMenu) return;
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [showUserMenu]);

  const handleTheme = (theme: Theme) => {
    document.documentElement.classList.remove('dark', 'high-contrast');
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else if (theme === 'high-contrast') document.documentElement.classList.add('high-contrast');
    localStorage.setItem('theme', theme);
    setShowThemeMenu(false);
  };

  const handleSignOut = () => {
    // Full-page navigation to the Hono worker's logout endpoint.
    // The worker clears the session cookies and redirects to the home page.
    window.location.href = '/auth/logout';
  };

  const initial = (signedInName || signedInEmail || '?').slice(0, 1).toUpperCase();
  const displayName = signedInName || (signedInEmail ? signedInEmail.split('@')[0] : '');

  return (
    <header className="dashboard-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="btn btn-secondary btn-sm mobile-menu-btn"
            aria-label="Toggle sidebar"
          >
            <Menu size={18} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {/* Theme switcher */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            className="btn btn-secondary btn-sm"
            aria-label="Change theme"
          >
            <Sun size={16} />
          </button>
          {showThemeMenu && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: '0.25rem',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--card-shadow)',
                overflow: 'hidden',
                zIndex: 50,
                minWidth: 140,
              }}
            >
              {[
                { theme: 'light' as Theme, icon: Sun, label: 'Light' },
                { theme: 'dark' as Theme, icon: Moon, label: 'Dark' },
                { theme: 'high-contrast' as Theme, icon: Eye, label: 'High Contrast' },
              ].map(({ theme, icon: Icon, label }) => (
                <button
                  key={theme}
                  onClick={() => handleTheme(theme)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Signed-in user menu */}
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu((v) => !v)}
            aria-label="Account menu"
            aria-expanded={showUserMenu}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--brand-primary)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8125rem',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {initial}
          </button>
          {showUserMenu && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: '0.375rem',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--card-shadow)',
                overflow: 'hidden',
                zIndex: 50,
                minWidth: 240,
              }}
            >
              {/* Identity block (read-only) */}
              {(displayName || signedInEmail) && (
                <div style={{ padding: '0.75rem 0.875rem', borderBottom: '1px solid var(--border-color)' }}>
                  {displayName && (
                    <div
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </div>
                  )}
                  {signedInEmail && (
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-tertiary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.3,
                        marginTop: 2,
                      }}
                      title={signedInEmail}
                    >
                      {signedInEmail}
                    </div>
                  )}
                  {signedInAuthMethod.startsWith('sso:') && (
                    <div
                      style={{
                        fontSize: '0.625rem',
                        color: 'var(--brand-primary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginTop: 4,
                        fontWeight: 600,
                      }}
                    >
                      via SSO
                    </div>
                  )}
                </div>
              )}

              {/* Sign out */}
              <button
                onClick={handleSignOut}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 0.875rem',
                  width: '100%',
                  border: 'none',
                  background: 'none',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  textAlign: 'left',
                }}
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
